import React, { useMemo, useCallback } from "react";
import { Text, Stack, useMantineTheme } from "@mantine/core";
import { VirtualizedList, VirtualizedListItemProps } from "../../../generic";
import { EventPreviewCard, ParsedEvent } from "../EventPreviewCard";
import { AppIcon } from "../../../generic";

export interface EventImportListProps {
  /** Array of parsed events to display */
  events: ParsedEvent[];
  /** Set of selected event IDs */
  selectedEventIds: Set<string>;
  /** Set of event IDs currently being added */
  addingEventIds: Set<string>;
  /** Whether the list is disabled */
  disabled?: boolean;
  /** Whether selection is enabled */
  enableSelection?: boolean;
  /** Height of the list container */
  height?: number;
  /** Width of the list container */
  width?: number | string;
  /** Height of each event card */
  itemHeight?: number;
  /** Loading state */
  loading?: boolean;
  /** Callback when event selection changes */
  onSelectionChange?: (eventId: string, selected: boolean) => void;
  /** Callback when an individual event should be added */
  onAddEvent?: (event: ParsedEvent) => void;
  /** Callback when scrolled near bottom (for pagination) */
  onLoadMore?: () => void;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Custom loading message */
  loadingMessage?: string;
}

export const EventImportList: React.FC<EventImportListProps> = ({
  events,
  selectedEventIds,
  addingEventIds,
  disabled = false,
  enableSelection = true,
  height = 500,
  width = "100%",
  itemHeight = 200,
  loading = false,
  onSelectionChange,
  onAddEvent,
  onLoadMore,
  emptyMessage = "No events found in the calendar file",
  loadingMessage = "Loading events...",
}) => {
  const theme = useMantineTheme();

  // Handle selection change for individual events
  const handleSelectionChange = useCallback(
    (eventId: string, selected: boolean) => {
      if (disabled) return;
      onSelectionChange?.(eventId, selected);
    },
    [disabled, onSelectionChange]
  );

  // Handle add individual event
  const handleAddEvent = useCallback(
    (event: ParsedEvent) => {
      if (disabled) return;
      onAddEvent?.(event);
    },
    [disabled, onAddEvent]
  );

  // Render each event item in the virtualized list
  const renderEventItem = useCallback(
    ({
      item,
      index,
      style,
      ariaAttributes,
    }: VirtualizedListItemProps<ParsedEvent>) => {
      const isSelected = selectedEventIds.has(item.tempId);
      const isAdding = addingEventIds.has(item.tempId);

      return (
        <EventPreviewCard
          event={item}
          index={index}
          selected={isSelected}
          disabled={disabled}
          adding={isAdding}
          enableSelection={enableSelection}
          onSelectionChange={handleSelectionChange}
          onAdd={handleAddEvent}
          style={style}
          ariaAttributes={ariaAttributes}
        />
      );
    },
    [
      selectedEventIds,
      addingEventIds,
      disabled,
      enableSelection,
      handleSelectionChange,
      handleAddEvent,
    ]
  );

  // Loading component
  const loadingComponent = useMemo(
    () => (
      <Stack align="center" gap="md">
        <AppIcon
          iconName="Loader2"
          size={32}
          style={{
            color: theme.colors.blue[6],
            animation: "spin 1s linear infinite",
          }}
        />
        <Text size="sm" c="dimmed">
          {loadingMessage}
        </Text>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </Stack>
    ),
    [theme.colors.blue, loadingMessage]
  );

  // Empty state component
  const emptyComponent = useMemo(
    () => (
      <Stack align="center" gap="md">
        <AppIcon
          iconName="Calendar"
          size={48}
          style={{ color: theme.colors.gray[4] }}
        />
        <Stack align="center" gap="xs">
          <Text size="lg" fw={600} c="dimmed">
            No Events Found
          </Text>
          <Text size="sm" c="dimmed" ta="center" maw={400}>
            {emptyMessage}
          </Text>
        </Stack>
      </Stack>
    ),
    [theme.colors.gray, emptyMessage]
  );

  // Handle scroll to bottom for potential pagination
  const handleScrollToBottom = useCallback(() => {
    if (!loading && onLoadMore) {
      onLoadMore();
    }
  }, [loading, onLoadMore]);

  // Statistics for debugging/info
  const stats = useMemo(() => {
    const totalEvents = events.length;
    const selectedCount = selectedEventIds.size;
    const addingCount = addingEventIds.size;

    return {
      total: totalEvents,
      selected: selectedCount,
      adding: addingCount,
    };
  }, [events.length, selectedEventIds.size, addingEventIds.size]);

  return (
    <Stack gap="xs">
      {/* List statistics (optional, can be hidden in production) */}
      {process.env.NODE_ENV === "development" && events.length > 0 && (
        <Text size="xs" c="dimmed">
          Events: {stats.total} • Selected: {stats.selected} • Adding:{" "}
          {stats.adding}
        </Text>
      )}

      {/* Virtualized list */}
      <VirtualizedList
        items={events}
        itemHeight={itemHeight}
        height={height}
        width={width}
        renderItem={renderEventItem}
        loading={loading}
        loadingComponent={loadingComponent}
        emptyComponent={emptyComponent}
        onScrollToBottom={onLoadMore ? handleScrollToBottom : () => {}}
        scrollToBottomThreshold={200} // Trigger when 200px from bottom
        overscanCount={3} // Render 3 extra items outside viewport
        style={{
          backgroundColor: theme.colors.gray[0],
        }}
      />

      {/* Additional info for large lists */}
      {events.length > 100 && (
        <Text size="xs" c="dimmed" ta="center">
          Showing {events.length} events • Use checkboxes to select multiple
          events for bulk import
        </Text>
      )}
    </Stack>
  );
};
